import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { eligibility } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  EVENT_READING,
  IMPACT_EXPLANATION,
  REASON_CATEGORY_READING,
  REASON_WITHHELD_EXPLANATION,
  RESTORATION_READING,
  SCOPE_READING,
  formatWhen,
  messageOf,
  placedByReading,
  reasonReading,
  restorationReading,
  restorationSentence,
  statusPill,
  impactKindLabel,
} from './eligibilityLogic.js';

/**
 * One worker's eligibility: whether they can work, every hold in force, what a
 * hold has put in question, and — for whoever may manage the workforce — the
 * append-only history and the button that lifts a hold.
 *
 * FOUR THINGS THIS SCREEN REFUSES TO COLLAPSE.
 *
 *   1. A 404 HERE IS NOT "NO SUCH WORKER". The route answers 404 for a worker
 *      outside the caller's reach in the same words an unknown uuid gets,
 *      deliberately, so the pair cannot be used to enumerate the workforce. The
 *      copy therefore names both worlds and picks neither.
 *   2. WITHHELD IS NOT ABSENT. Without `directory:manage` the reason fields are
 *      not on the wire at all and `reasonWithheld` says so; the screen prints
 *      "Withheld" and an explanation, never a blank.
 *   3. `restorableWithoutManagement === null` IS NOT "NO". It is null for an
 *      ELIGIBLE worker, where the question was never asked.
 *   4. IMPACT IS A READ. The domain surfaces and forecasts what a hold puts in
 *      question; it does not cancel anything, and there is no button here that
 *      would. What happens to a client's booked visit is management's call.
 */
export function EligibilityWorkerDetail() {
  const params = useParams({ strict: false }) as { workerId?: string };
  const workerId = params.workerId ?? '';
  const qc = useQueryClient();
  const navigate = useNav();
  const { has } = usePermissions();
  const canManage = has(eligibility.ELIGIBILITY_MANAGE);

  const [liftingId, setLiftingId] = useState<string | null>(null);
  const [liftReason, setLiftReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['eligibility', 'worker', workerId],
    queryFn: () => eligibility.worker(workerId),
    enabled: workerId !== '',
  });

  const history = useQuery({
    queryKey: ['eligibility', 'worker', workerId, 'history'],
    queryFn: () => eligibility.history(workerId),
    // Every row of the history carries a reason, so the route needs
    // `directory:manage` and there is no reduced projection of it. Asking
    // without the key would produce a 403 the reader can do nothing about.
    enabled: workerId !== '' && canManage,
  });

  const lift = useMutation({
    mutationFn: (holdId: string) => eligibility.liftHold(holdId, { reason: liftReason.trim() }),
    onSuccess: () => {
      setLiftingId(null);
      setLiftReason('');
      setFormError(null);
      void qc.invalidateQueries({ queryKey: ['eligibility'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 960 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <LoadFailure
          error={detail.error}
          mainRead
          // The 'unreachable' branch only. A 403 and a 401 get LoadFailure's
          // own sentences, which are the right ones for each.
          message="We couldn’t load this worker’s eligibility just now. It may also be that this worker is not in your organization, or not one your account reaches — the server answers those two the same way on purpose, so that a uuid cannot be used to find out who works here."
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const data = detail.data;
  const restoration = restorationReading(data.restorableWithoutManagement);

  const holdColumns: Column<eligibility.HoldView>[] = [
    { key: 'scope', header: 'Reaches', render: (h) => SCOPE_READING[h.scopeType] },
    {
      key: 'reason',
      header: 'Reason',
      render: (h) => {
        const reading = reasonReading(h);
        if (reading.kind === 'withheld') {
          return <span className="rn-muted">Withheld</span>;
        }
        return (
          <span>
            {reading.label}
            {reading.kind === 'present' ? (
              <>
                <br />
                <span className="rn-muted">{reading.note}</span>
              </>
            ) : (
              <>
                <br />
                <span className="rn-muted">No note was left beside it.</span>
              </>
            )}
          </span>
        );
      },
    },
    {
      key: 'restoration',
      header: 'How it lifts',
      render: (h) => RESTORATION_READING[h.restorationMode],
    },
    { key: 'placedBy', header: 'Placed by', render: (h) => placedByReading(h) },
    { key: 'placedAt', header: 'Placed', render: (h) => formatWhen(h.placedAt) },
    {
      key: 'status',
      header: 'Status',
      render: (h) => {
        const pill = statusPill(h);
        return (
          <Pill variant={pill.variant} dot>
            {pill.label}
          </Pill>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (h) =>
        canManage && h.status === 'active' ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => {
              setLiftingId(h.id);
              setLiftReason('');
              setFormError(null);
            }}
          >
            Lift
          </button>
        ) : null,
    },
  ];

  const impactColumns: Column<eligibility.EligibilityImpact>[] = [
    { key: 'kind', header: 'What', render: (i) => impactKindLabel(i.kind) },
    { key: 'starts', header: 'Starts', render: (i) => formatWhen(i.startsAt) },
    {
      key: 'client',
      header: 'Client',
      // An id, not a name: this endpoint returns a client id and nothing else,
      // and looking the name up from a differently scoped directory read would
      // put a client's name on screen for a reader the client endpoint might
      // refuse. The id is enough to open the record where names are allowed.
      render: (i) => (i.clientId === null ? 'Not about a client' : i.clientId),
    },
  ];

  const events = history.data?.events ?? [];

  return (
    <div className="rn-col" style={{ maxWidth: 960 }}>
      <SettingsHeader
        title="Eligibility"
        blurb="Whether this worker may do service work right now, and what would change it. Eligibility is not employment: a person can be employed and ineligible, and on their way back."
      />

      <div>
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => navigate('/workforce/eligibility')}
        >
          {'‹'} All holds
        </button>
      </div>

      <SettingsCard>
        <div className="cmp-control-head">
          <h2 className="rn-card-title">Current state</h2>
          <Pill variant={data.eligible ? 'good' : 'urgent'} dot>
            {data.eligible ? 'Eligible' : 'Restricted'}
          </Pill>
        </div>
        {data.workerMessage === null ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Nothing is standing in this worker’s way. Every protected part of the app answers them
            normally.
          </p>
        ) : (
          <>
            {/* The worker's OWN sentence, shown to management verbatim so they
                know exactly what the person on the other end is reading. It
                carries no reason, no scope and no date, by design. */}
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              This is the whole of what the worker is told, word for word:
            </p>
            <blockquote
              style={{
                margin: 'var(--space-8) 0 0',
                paddingLeft: 'var(--space-12)',
                borderLeft: '2px solid var(--color-line)',
              }}
            >
              {data.workerMessage}
            </blockquote>
          </>
        )}
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          {restorationSentence(restoration)}
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Holds</h2>
        {data.holds.length === 0 ? (
          <SuccessEmpty
            heading="No hold in force"
            message="Nothing is currently restricting this worker."
          />
        ) : (
          <>
            {data.holds.some((h) => h.reasonWithheld) ? (
              <p className="rn-muted" style={{ marginBottom: 'var(--space-8)' }}>
                {REASON_WITHHELD_EXPLANATION}
              </p>
            ) : null}
            <TableCard
              columns={holdColumns}
              rows={data.holds}
              rowKey={(h) => h.id}
              caption="Eligibility holds for this worker"
            />
          </>
        )}

        {liftingId !== null && canManage ? (
          <form
            className="rn-col"
            style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (liftReason.trim() !== '') lift.mutate(liftingId);
            }}
          >
            <SettingsField
              label="Why this hold is being lifted"
              hint="Required. The hold is never deleted — lifting it is an event with your name on it, and the row keeps saying the restriction happened."
            >
              <textarea
                rows={3}
                value={liftReason}
                onChange={(e) => setLiftReason(e.currentTarget.value)}
              />
            </SettingsField>
            {formError ? (
              <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
                {formError}
              </p>
            ) : null}
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <button
                type="submit"
                className="rn-btn"
                disabled={liftReason.trim() === '' || lift.isPending}
              >
                Lift the hold
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setLiftingId(null);
                  setFormError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </SettingsCard>

      {/* Impact. Empty when the worker is eligible, because the server does not
          compute it for somebody who is not restricted — which is an ABSENT
          answer, not a measured zero, and the copy says so rather than claiming
          nothing is affected. */}
      <SettingsCard>
        <h2 className="rn-card-title">What this has put in question</h2>
        {data.eligible ? (
          <p className="rn-muted">
            Nothing was looked at. Impact is only worked out for a worker who is restricted, so this
            is not a claim that their schedule is empty.
          </p>
        ) : data.impact.length === 0 ? (
          <SuccessEmpty
            heading="Nothing booked is affected"
            message="This worker has no upcoming shifts or assignments that the hold puts in question."
          />
        ) : (
          <>
            <p className="rn-muted" style={{ marginBottom: 'var(--space-8)' }}>
              {IMPACT_EXPLANATION}
            </p>
            <TableCard
              columns={impactColumns}
              rows={data.impact}
              rowKey={(i) => `${i.kind}:${i.id}`}
              caption="Work this hold has put in question"
            />
          </>
        )}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">History</h2>
        {!canManage ? (
          <Refused
            heading="Withheld, not empty"
            message={`Every row of this history carries the reason a hold was placed or lifted, which is employment information about a named person. It needs the ${eligibility.ELIGIBILITY_MANAGE} permission. There may well be history here.`}
          />
        ) : history.isError ? (
          <LoadFailure
            error={history.error}
            message="We couldn’t load this worker’s eligibility history just now. Do not read this as “nothing has happened”."
            onRetry={() => void history.refetch()}
          />
        ) : history.isLoading ? (
          <SkeletonSet surface="table" rows={3} />
        ) : events.length === 0 ? (
          <SuccessEmpty
            heading="Nothing has happened"
            message="No hold has ever been placed on this worker."
          />
        ) : (
          <ol
            className="rn-col"
            style={{ gap: 'var(--space-12)', paddingLeft: 0, listStyle: 'none' }}
          >
            {events.map((event) => (
              <li key={event.id} style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <span>
                  <strong>{EVENT_READING[event.event_type]}</strong>
                  {' · '}
                  {REASON_CATEGORY_READING[event.reason_category]}
                  {' · '}
                  {SCOPE_READING[event.scope_type]}
                </span>
                <span className="rn-muted">
                  {formatWhen(event.occurred_at)} ·{' '}
                  {event.by_system ? 'Phibian (requirement sweep)' : 'A person'}
                </span>
                {event.detail ? <span className="rn-muted">{event.detail}</span> : null}
              </li>
            ))}
          </ol>
        )}
      </SettingsCard>
    </div>
  );
}
